import { LoadComponent } from '@components/lazy-loader';
import { lazy, Suspense } from 'react';
import {
  isChunkError,
  recoverFromChunkError,
} from '@/lib/chunk-error-recovery';

// Catch stale-chunk 404s at the import boundary so React never sees a render
// error. Without this, the rejected `import()` bubbles through `lazy()` into
// the nearest error boundary, which paints its fallback ("Aaaah! Something
// went wrong") for the brief window before the global `unhandledrejection`
// listener fires `window.location.reload()`. Returning a never-resolving
// promise here keeps React in Suspense — showing the existing `LoadComponent`
// spinner — until the reload swaps the page.
//
// If `recoverFromChunkError()` returns `false` it means the dedupe guard
// suppressed the reload (a reload already fired within RELOAD_DEDUPE_MS).
// In that case we re-throw so the error boundary handles it normally —
// otherwise the user would be stuck on the spinner with no escape hatch.
const importWithChunkRecovery = componentImport => () =>
  componentImport().catch(error => {
    if (isChunkError(error?.message) && recoverFromChunkError()) {
      return new Promise(() => {});
    }
    throw error;
  });

const loadable = componentImport => {
  const LazyComponent = lazy(importWithChunkRecovery(componentImport));

  const LoadableComponent = props => (
    <Suspense fallback={<LoadComponent />}>
      <LazyComponent {...props} />
    </Suspense>
  );

  return LoadableComponent;
};

export default loadable;
